import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { UseFormReturn, useForm } from 'react-hook-form'
import { TFieldValues } from 'pages/configuration/LmsProfiles/common/types'
import { profileFormValidations } from 'pages/configuration/LmsProfiles/common/validations'
import { RouteProfileEditParams } from 'enum/routes'
import { showProfile } from 'api/administration/lms-profiles/show'
import { ILmsProfileModel } from 'models/lmsProfile.model'
import updateLmsProfile from 'api/lms-profiles/Edit'
import { SuccessToast } from 'components/ui/toast/toastsTemplates'
import { UserAttributes } from 'api/administration/participants/types'
import { Segment } from 'models/segment.model'
import { LmsProfileTypes } from 'enum/lms-profile-types.enum'
import createProfileUsers from 'api/administration/lms-profiles/users/create'
import destroyManyProfileUsers from 'api/administration/lms-profiles/users/destroyMany'
import createProfileSegments from 'api/administration/lms-profiles/segments/create'
import destroyManyProfileSegments from 'api/administration/lms-profiles/segments/destroyMany'

interface ICurrentValues {
  name?: string
  color?: string
  profileType?: LmsProfileTypes | null
}

type ProfileValues = TFieldValues

type EditProfileForm = Pick<
  UseFormReturn<ProfileValues, any>,
  'control' | 'getValues' | 'setValue' | 'formState' | 'watch'
> & {
  /**
   * This function is used to indicate that one or more fields have been updated and that the form should be revalidated.
   * @param name
   */
  trigger(
    name?: keyof ProfileValues | (keyof ProfileValues)[]
  ): Promise<boolean>
}

interface OnSaveParams {
  silent?: boolean
  values?: Partial<ProfileValues>
}

type EditProfileValues = {
  form: EditProfileForm
  profile: ILmsProfileModel | null
  loading: boolean
  updatedData: boolean
  onSave: (params?: OnSaveParams) => Promise<void>
  usersAdded: UserAttributes[]
  setUsersAdded: React.Dispatch<React.SetStateAction<UserAttributes[]>>
  removedUsers: number[]
  setRemovedUsers: React.Dispatch<React.SetStateAction<number[]>>
  segmentsAdded: Segment[]
  setSegmentsAdded: React.Dispatch<React.SetStateAction<Segment[]>>
  removedSegments: number[]
  setRemovedSegments: React.Dispatch<React.SetStateAction<number[]>>
}

export const EditProfileContext = createContext<EditProfileValues>(
  Object.create(null)
)

export const EditProfileProvider: React.FC = ({ children }) => {
  const { id } = useParams<RouteProfileEditParams>()
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [updatedData, setUpdatedData] = useState(false)
  const [profile, setProfile] = useState<ILmsProfileModel | null>(null)
  const [usersAdded, setUsersAdded] = useState<UserAttributes[]>([])
  const [removedUsers, setRemovedUsers] = useState<number[]>([])
  const [segmentsAdded, setSegmentsAdded] = useState<Segment[]>([])
  const [removedSegments, setRemovedSegments] = useState<number[]>([])

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true)
      try {
        if (!id) return
        const data = await showProfile(id)
        setProfile(data)
      } catch (e) {
        console.error(e)
      }
      setLoading(false)
    }
    fetchProfile()
  }, [id])

  const { control, getValues, formState, setValue, watch, trigger } =
    useForm<ProfileValues>({
      defaultValues: {
        name: '',
        color: '#000000',
        profileType: null,
      },
      resolver: (data) => {
        const errors = profileFormValidations(data)

        return {
          errors,
          values: data,
        }
      },
      reValidateMode: 'onChange',
      mode: 'all',
    })

  useEffect(() => {
    const subscription = watch((currentValues) => {
      if (!profile) return

      setUpdatedData(
        hasInputsChanged(currentValues, profile) ||
          hasAssignmentsChanged(currentValues.profileType)
      )
    })

    return () => subscription.unsubscribe()
  }, [watch, profile])

  useEffect(() => {
    if (!profile) return
    const currentValues = getValues()

    setUpdatedData(
      hasInputsChanged(currentValues, profile) ||
        hasAssignmentsChanged(currentValues.profileType)
    )
  }, [usersAdded, removedUsers, segmentsAdded, removedSegments, profile])

  const hasInputsChanged = (
    currentValues: ICurrentValues,
    profile: ILmsProfileModel
  ) => {
    const isModified =
      currentValues.name !== profile.name ||
      currentValues.color !== profile.color ||
      currentValues.profileType !== profile.profileType

    return isModified
  }

  const hasAssignmentsChanged = (
    profileType: LmsProfileTypes | null | undefined
  ) => {
    switch (profileType) {
      case LmsProfileTypes.participants:
        return usersAdded.length > 0 || removedUsers.length > 0
      case LmsProfileTypes.segments:
        return segmentsAdded.length > 0 || removedSegments.length > 0
      default:
        return false
    }
  }

  const onSave = useCallback(
    async (params?: OnSaveParams) => {
      if (!formState.isValid || !profile) return

      let { name, color, profileType } = { ...params?.values }

      const current = getValues()

      name ||= current.name
      color ||= current.color
      profileType ||= current.profileType

      await updateLmsProfile({
        id: profile.id,
        name,
        color,
        profileType: profileType ?? profile.profileType,
      })
      switch (profileType ?? profile.profileType) {
        case LmsProfileTypes.participants:
          if (usersAdded.length) {
            await createProfileUsers(
              profile.id,
              usersAdded.map(({ id }) => id)
            )
          }
          if (removedUsers.length) {
            const usersQuery = {
              full_name_or_identifier_or_position_or_lms_profiles_name_or_area_name_cont:
                '',
              g: [],
            }
            await destroyManyProfileUsers(profile.id, {
              ...usersQuery,
              ...{ id_in: removedUsers },
            })
          }
          break
        case LmsProfileTypes.segments:
          if (segmentsAdded.length) {
            await createProfileSegments(
              profile.id,
              segmentsAdded.map(({ id }) => id)
            )
          }
          if (removedSegments.length) {
            const segmentsQuery = { name_cont: '', g: [] }
            await destroyManyProfileSegments(profile.id, {
              ...segmentsQuery,
              ...{ id_in: removedSegments },
            })
          }
          break
      }
      if (!params?.silent)
        SuccessToast(t('administration.toastMessages.profile.updateSuccess'))
    },
    [
      formState.isValid,
      getValues,
      profile,
      t,
      usersAdded,
      removedUsers,
      segmentsAdded,
      removedSegments,
    ]
  )

  useEffect(() => {
    if (!profile) return
    setValue('name', profile.name)
    setValue('color', profile.color)
    setValue('profileType', profile.profileType)
    // Validate the form when the profile changes
    trigger()
  }, [profile, setValue, trigger])

  return (
    <EditProfileContext.Provider
      value={{
        form: {
          control,
          getValues,
          setValue,
          formState,
          watch,
          trigger,
        },
        loading,
        updatedData,
        profile,
        onSave,
        usersAdded,
        setUsersAdded,
        removedUsers,
        setRemovedUsers,
        segmentsAdded,
        setSegmentsAdded,
        removedSegments,
        setRemovedSegments,
      }}
    >
      {children}
    </EditProfileContext.Provider>
  )
}

export const useEditProfile = () => useContext(EditProfileContext)
